import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import { colorPalette } from "../styles/theme";
import seededRandom from "./seededRandom";

/**
 * 종합현황 하단 보조 시각화 (2열 Grid)
 * - 좌측: 참가자 마음 건강 위험도 분포 (도넛) — 구간 클릭 시 참가자 관리(해당 등급 필터)로 이동
 * - 우측: 일별 참여 인원(막대) + 에피소드 발생 건수(빨간 꺾은선) 복합 차트 (이중 Y축)
 *
 * 실제 API 연동 전까지는 KPI 카드와 숫자가 맞도록 summary 기준으로 가상 데이터를 만듭니다.
 */

// 두 차트를 나란히 둘 수 있는 최소 본문 폭 — 이보다 좁으면 위아래로 쌓음
const CHART_TWO_COLUMN_MIN_WIDTH = 880;

const EPISODE_COLOR = "#EF4444";
const BAR_COLOR = colorPalette.primary;

// 기간 평균 디스트레스 4단계 (개인 상세 DISTRESS_LEVELS, 카드4 "기관 평균 마음 상태"와 같은 구간)
const DISTRESS_SEGMENTS = [
  { key: "stable", label: "🟢 정상/안정", color: "#22C55E" },
  { key: "caution", label: "🟡 경미/주의", color: "#FACC15" },
  { key: "warning", label: "🟠 중등도/경고", color: "#F97316" },
  { key: "high", label: "🔴 중증/고위험", color: "#EF4444" },
];

// ─── 날짜 유틸 ─────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const toDateString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const getDateList = (start, end) => {
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  const dates = [];
  for (let d = new Date(sy, sm - 1, sd); d <= new Date(ey, em - 1, ed); d.setDate(d.getDate() + 1)) {
    dates.push(toDateString(d));
  }
  return dates;
};

/** y축 상한을 보기 좋은 값(1·2·5 × 10ⁿ 간격 4칸)으로 올림 */
const niceAxis = (max, ticks = 4) => {
  const raw = Math.max(max, 1) / ticks;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw);
  const niceStep = Math.max(1, step);
  return { max: niceStep * ticks, step: niceStep };
};

// ─── 가상 데이터 (API 연동 전 임시) ──────────────────────────────────
/** 활동 인원을 4단계로 나눔 — 고위험 인원은 카드4의 디스트레스 고위험군과 일치 */
const buildDistribution = (scopeKey, range, summary) => {
  const rand = seededRandom(`${scopeKey}|${range.start}|${range.end}|distribution`);
  const total = summary.org_respondents;
  const high = Math.min(summary.distress_high_risk_count, total);
  const rest = total - high;
  const stable = Math.round(rest * (0.4 + rand() * 0.2));
  const caution = Math.round((rest - stable) * (0.55 + rand() * 0.2));
  const counts = { stable, caution, warning: rest - stable - caution, high };

  return DISTRESS_SEGMENTS.map((segment) => ({
    ...segment,
    count: counts[segment.key],
    percent: total ? Math.round((counts[segment.key] / total) * 100) : 0,
  }));
};

/** 일별 참여 인원·에피소드 건수 — 에피소드 합계는 카드3의 에피소드 건수와 일치 */
const buildDailySeries = (scopeKey, range, summary) => {
  const rand = seededRandom(`${scopeKey}|${range.start}|${range.end}|daily`);
  const dates = getDateList(range.start, range.end);
  const weights = dates.map(() => rand());
  const weightSum = weights.reduce((a, b) => a + b, 0) || 1;

  // 가중치 비율대로 에피소드 합계를 나누고, 반올림 오차는 가중치가 큰 날부터 보정
  const episodes = weights.map((w) => Math.floor((w / weightSum) * summary.episode_total));
  let remainder = summary.episode_total - episodes.reduce((a, b) => a + b, 0);
  [...weights.keys()]
    .sort((a, b) => weights[b] - weights[a])
    .forEach((i) => {
      if (remainder > 0) {
        episodes[i] += 1;
        remainder -= 1;
      }
    });

  return dates.map((date, i) => ({
    date,
    participants: Math.round(summary.org_respondents * (0.45 + rand() * 0.4)),
    episodes: episodes[i],
  }));
};

// ─── 공통 UI ───────────────────────────────────────────────────────
const ChartCard = ({ title, description, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, backgroundColor: colorPalette.white }}>
    <Typography sx={{ fontSize: 15, fontWeight: 700, color: colorPalette.textPrimary }}>{title}</Typography>
    <Typography sx={{ mt: 0.5, mb: 2, fontSize: 12, color: colorPalette.textSecondary }}>{description}</Typography>
    {children}
  </Paper>
);

const ChartTooltip = ({ left, top, alignRight, children }) => (
  <Box
    sx={{
      position: "absolute",
      left,
      top,
      transform: `translate(${alignRight ? "-100%" : "0"}, 0)`,
      ml: alignRight ? -1.5 : 1.5,
      px: 1.25,
      py: 0.9,
      borderRadius: 1,
      backgroundColor: colorPalette.textPrimary,
      color: colorPalette.white,
      fontSize: 12,
      lineHeight: 1.6,
      whiteSpace: "nowrap",
      pointerEvents: "none",
      boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
      zIndex: 1,
    }}
  >
    {children}
  </Box>
);

// ─── 좌측: 위험도 분포 도넛 ─────────────────────────────────────────
const DONUT = { size: 200, thickness: 30, gapPx: 2 };

/** 시작/끝 각도(라디안, 12시 방향 0)로 도넛 조각 path 생성 */
const arcPath = (cx, cy, rOuter, rInner, startAngle, endAngle) => {
  const point = (r, angle) => [cx + r * Math.sin(angle), cy - r * Math.cos(angle)];
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  const [x1, y1] = point(rOuter, startAngle);
  const [x2, y2] = point(rOuter, endAngle);
  const [x3, y3] = point(rInner, endAngle);
  const [x4, y4] = point(rInner, startAngle);
  return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4} Z`;
};

const DistressDonutChart = ({ data, total, onSelect }) => {
  const [hoveredKey, setHoveredKey] = useState(null);
  const { size, thickness, gapPx } = DONUT;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 4; // 호버 시 살짝 커지는 여유
  const rInner = rOuter - thickness;

  // 조각 사이 2px 간격을 각도로 환산 (조각이 하나뿐이면 간격 없음)
  const visible = data.filter((d) => d.count > 0);
  const gapAngle = visible.length > 1 ? gapPx / rOuter : 0;

  let cursor = 0;
  const arcs = visible.map((d) => {
    const sweep = (d.count / total) * Math.PI * 2;
    const arc = { ...d, start: cursor + gapAngle / 2, end: cursor + sweep - gapAngle / 2 };
    cursor += sweep;
    return arc;
  });

  const hovered = data.find((d) => d.key === hoveredKey);

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 3 }}>
      <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0, mx: "auto" }}>
        <svg width={size} height={size} role="img" aria-label="참가자 마음 건강 위험도 분포">
          {total === 0 ? (
            <circle cx={cx} cy={cy} r={rOuter - thickness / 2} fill="none" stroke={colorPalette.secondary} strokeWidth={thickness} />
          ) : (
            arcs.map((arc) => {
              const isHovered = hoveredKey === arc.key;
              // 360° 한 조각은 path로 그릴 수 없어 원으로 대체
              const full = arc.end - arc.start >= Math.PI * 2 - 0.0001;
              const common = {
                fill: full ? "none" : arc.color,
                opacity: hoveredKey && !isHovered ? 0.35 : 1,
                style: { cursor: "pointer", transition: "opacity 0.15s ease" },
                onMouseEnter: () => setHoveredKey(arc.key),
                onMouseLeave: () => setHoveredKey(null),
                onClick: () => onSelect(arc.key),
              };
              return full ? (
                <circle key={arc.key} cx={cx} cy={cy} r={rOuter - thickness / 2} stroke={arc.color} strokeWidth={thickness} {...common} />
              ) : (
                <path
                  key={arc.key}
                  d={arcPath(cx, cy, isHovered ? rOuter + 3 : rOuter, rInner, arc.start, arc.end)}
                  {...common}
                />
              );
            })
          )}
        </svg>

        {/* 가운데: 호버한 구간 또는 전체 활동 인원 */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ fontSize: 12, color: colorPalette.textSecondary }}>
            {hovered ? hovered.label : "전체 활동 인원"}
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: colorPalette.textPrimary, lineHeight: 1.2 }}>
            {hovered ? hovered.count : total}
            <Box component="span" sx={{ fontSize: 14, fontWeight: 600, ml: 0.25 }}>
              명
            </Box>
          </Typography>
          {hovered && (
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: colorPalette.textSecondary }}>
              {hovered.percent}%
            </Typography>
          )}
        </Box>
      </Box>

      {/* 범례 — 차트 조각과 동일하게 클릭 시 해당 등급으로 이동 */}
      <Box sx={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {data.map((d) => (
          <ButtonBase
            key={d.key}
            onClick={() => onSelect(d.key)}
            onMouseEnter={() => setHoveredKey(d.key)}
            onMouseLeave={() => setHoveredKey(null)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              textAlign: "left",
              backgroundColor: hoveredKey === d.key ? colorPalette.background : "transparent",
            }}
          >
            {/* 라벨 앞 이모지(🟢🟡🟠🔴)가 색 표시를 겸하므로 별도 색 칩은 두지 않음 */}
            <Typography sx={{ flex: 1, fontSize: 13, color: colorPalette.textPrimary }}>{d.label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary, whiteSpace: "nowrap" }}>
              {d.count}명
            </Typography>
            <Typography sx={{ width: 40, textAlign: "right", fontSize: 12, color: colorPalette.textSecondary }}>
              {d.percent}%
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};

// ─── 우측: 일별 참여 인원(막대) + 에피소드(꺾은선) 복합 차트 ─────────────
const COMBO = { height: 240, padding: { top: 12, right: 40, bottom: 28, left: 40 } };

/** 부모 폭을 따라가는 차트용 — 요소의 현재 가로 폭을 추적 */
const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

const DailyComboChart = ({ data }) => {
  const [containerRef, width] = useElementWidth();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { height, padding } = COMBO;

  const innerWidth = Math.max(0, width - padding.left - padding.right);
  const innerHeight = height - padding.top - padding.bottom;
  const step = data.length ? innerWidth / data.length : 0;
  const barWidth = Math.max(4, Math.min(28, step * 0.55));

  const leftAxis = niceAxis(Math.max(...data.map((d) => d.participants), 0));
  const rightAxis = niceAxis(Math.max(...data.map((d) => d.episodes), 0));
  const xCenter = (i) => padding.left + step * i + step / 2;
  const yLeft = (v) => padding.top + innerHeight - (v / leftAxis.max) * innerHeight;
  const yRight = (v) => padding.top + innerHeight - (v / rightAxis.max) * innerHeight;

  // 날짜 라벨이 겹치지 않도록 약 48px당 1개만 표시
  const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(1, Math.floor(innerWidth / 48))));
  const ticks = Array.from({ length: 5 }, (_, i) => i);
  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xCenter(i)} ${yRight(d.episodes)}`).join(" ");
  const hovered = hoveredIndex !== null ? data[hoveredIndex] : null;

  return (
    <Box>
      {/* 범례 — 각 계열이 어느 축을 쓰는지 함께 표시 */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 12, height: 10, borderRadius: "2px", backgroundColor: BAR_COLOR }} />
          <Typography sx={{ fontSize: 12, color: colorPalette.textPrimary }}>기록 참여 인원 (명 · 왼쪽 축)</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ position: "relative", width: 16, height: 10 }}>
            <Box sx={{ position: "absolute", top: 4, left: 0, right: 0, height: 2, backgroundColor: EPISODE_COLOR }} />
            <Box
              sx={{ position: "absolute", top: 1, left: 5, width: 8, height: 8, borderRadius: "50%", backgroundColor: EPISODE_COLOR, border: `1.5px solid ${colorPalette.white}` }}
            />
          </Box>
          <Typography sx={{ fontSize: 12, color: colorPalette.textPrimary }}>에피소드 발생 (건 · 오른쪽 축)</Typography>
        </Box>
      </Box>

      <Box ref={containerRef} sx={{ position: "relative", width: "100%", height }}>
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label="일별 참여 인원 및 에피소드 발생 추이">
            {/* 가로 그리드 + 좌/우 축 눈금 */}
            {ticks.map((t) => {
              const y = padding.top + innerHeight - (t / 4) * innerHeight;
              return (
                <g key={t}>
                  <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke={colorPalette.border} strokeDasharray={t === 0 ? undefined : "3 4"} />
                  <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill={colorPalette.textSecondary}>
                    {leftAxis.step * t}
                  </text>
                  <text x={width - padding.right + 8} y={y + 4} textAnchor="start" fontSize="11" fill={colorPalette.textSecondary}>
                    {rightAxis.step * t}
                  </text>
                </g>
              );
            })}

            {/* 호버한 날짜 강조 배경 */}
            {hovered && (
              <rect x={padding.left + step * hoveredIndex} y={padding.top} width={step} height={innerHeight} fill={colorPalette.secondary} opacity="0.7" />
            )}

            {/* 막대: 기록 참여 인원 (위쪽만 4px 둥글게) */}
            {data.map((d, i) => {
              const x = xCenter(i) - barWidth / 2;
              const y = yLeft(d.participants);
              const h = padding.top + innerHeight - y;
              const r = Math.min(4, barWidth / 2, h);
              return (
                <path
                  key={d.date}
                  d={`M ${x} ${y + h} V ${y + r} Q ${x} ${y} ${x + r} ${y} H ${x + barWidth - r} Q ${x + barWidth} ${y} ${x + barWidth} ${y + r} V ${y + h} Z`}
                  fill={BAR_COLOR}
                  opacity={hoveredIndex === null || hoveredIndex === i ? 0.85 : 0.45}
                />
              );
            })}

            {/* 꺾은선: 에피소드 발생 건수 */}
            <path d={linePath} fill="none" stroke={EPISODE_COLOR} strokeWidth="2" strokeLinejoin="round" />
            {data.map((d, i) => (
              <circle
                key={d.date}
                cx={xCenter(i)}
                cy={yRight(d.episodes)}
                r={hoveredIndex === i ? 5 : 4}
                fill={EPISODE_COLOR}
                stroke={colorPalette.white}
                strokeWidth="2"
              />
            ))}

            {/* x축 날짜 라벨 */}
            {data.map((d, i) =>
              i % labelEvery === 0 ? (
                <text key={d.date} x={xCenter(i)} y={height - 8} textAnchor="middle" fontSize="11" fill={colorPalette.textSecondary}>
                  {d.date.slice(5)}
                </text>
              ) : null,
            )}

            {/* 날짜별 호버 영역 (막대보다 넓게) */}
            {data.map((d, i) => (
              <rect
                key={d.date}
                x={padding.left + step * i}
                y={padding.top}
                width={step}
                height={innerHeight}
                fill="transparent"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex((prev) => (prev === i ? null : prev))}
              />
            ))}
          </svg>
        )}

        {hovered && (
          <ChartTooltip
            left={xCenter(hoveredIndex)}
            top={padding.top}
            alignRight={xCenter(hoveredIndex) > width / 2}
          >
            <Box sx={{ fontWeight: 700 }}>{hovered.date}</Box>
            <Box>기록 참여 {hovered.participants}명</Box>
            <Box>에피소드 {hovered.episodes}건</Box>
          </ChartTooltip>
        )}
      </Box>
    </Box>
  );
};

/**
 * @param {string} scopeKey - 조회 범위 키
 * @param {object} range - 적용된 조회 기간 { start, end }
 * @param {object} summary - 종합현황 요약 (KPI 카드와 숫자를 맞추기 위해 사용)
 * @param {Function} onSelectDistressLevel - 도넛 구간 클릭 시 (levelKey) => void
 */
const OverviewCharts = ({ scopeKey, range, summary, onSelectDistressLevel }) => {
  const distribution = useMemo(() => buildDistribution(scopeKey, range, summary), [scopeKey, range, summary]);
  const daily = useMemo(() => buildDailySeries(scopeKey, range, summary), [scopeKey, range, summary]);
  const days = daily.length;

  return (
    <Box sx={{ mt: 3, containerType: "inline-size" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 2,
          [`@container (max-width: ${CHART_TWO_COLUMN_MIN_WIDTH - 1}px)`]: { gridTemplateColumns: "minmax(0, 1fr)" },
        }}
      >
        <ChartCard
          title="참가자 마음 건강 위험도 분포"
          description="기간 평균 디스트레스 수준 기준입니다. 구간을 누르면 해당 등급 참가자 목록으로 이동합니다."
        >
          <DistressDonutChart data={distribution} total={summary.org_respondents} onSelect={onSelectDistressLevel} />
        </ChartCard>

        <ChartCard
          title="일별 참여 인원 및 에피소드 발생 추이"
          description={`조회 기간(${days}일) 동안 날짜별 기록 참여 인원과 에피소드 발생 건수입니다.`}
        >
          <DailyComboChart data={daily} />
        </ChartCard>
      </Box>
    </Box>
  );
};

export default OverviewCharts;
